import { CalloutBlobItem } from "src/design-system/Callout";
import { getUrlToSettingsPage } from "src/router/urls";
import { SettingsSubpages } from "src/settings/SettingsSubpages";

// Marker the llm_inference backend embeds in the error message body when a
// BYOK provider call fails with a 401/403. Matches the agents
// `LLM_PROVIDER_BYOK_AUTH_ERROR` termination reason so the FE can surface the
// same "manage credentials" affordance in both places.
const BYOK_AUTH_ERROR_MARKER = /llm_provider_byok_auth_error/i;

export const isBYOKAuthError = (errorMsg: string | undefined): boolean =>
  !!errorMsg && BYOK_AUTH_ERROR_MARKER.test(errorMsg);

type Action = { text: string; onClick: () => void };

/**
 * Builds a "Manage AI credentials" action for the node error callout when
 * the error message matches a BYOK auth failure. Returns undefined otherwise
 * so callers can fall through to their default action.
 */
export const getBYOKAuthErrorAction = ({
  errorMsg,
  orgId,
  wsId,
  isByokEnabled,
  navigate,
}: {
  errorMsg: string | undefined;
  orgId: string | undefined;
  wsId: string | undefined;
  isByokEnabled: boolean;
  navigate: (to: string) => void;
}): Action | undefined => {
  // Mirror the route gate in Router.tsx: the AgentCredentials settings page is
  // only registered when BYOK is enabled (feature gate, or the legacy flag
  // during migration). A node with a persisted `credential_id` can still
  // surface a BYOK auth error while BYOK is off (rollout revert, per-workspace
  // gate), so don't offer an action that navigates to a route the router
  // doesn't serve.
  if (!isBYOKAuthError(errorMsg) || !orgId || !wsId || !isByokEnabled)
    return undefined;
  return {
    text: "Manage AI credentials",
    onClick: () =>
      navigate(
        getUrlToSettingsPage(orgId, wsId, SettingsSubpages.AgentCredentials),
      ),
  };
};

/**
 * Plain-language section shown at the top of the node error callout when the
 * raw error is a BYOK provider auth failure. The raw provider blob is still
 * rendered below it — this just explains what went wrong in human terms.
 *
 * Not gated on the feature flag: the explanation is useful even when the
 * "Manage AI credentials" action is hidden (e.g. flag off but a persisted
 * `credential_id` still fails at runtime).
 */
export const getBYOKAuthErrorSection = (
  errorMsg: string | undefined,
): CalloutBlobItem | undefined => {
  if (!isBYOKAuthError(errorMsg)) return undefined;
  return {
    title: "Custom AI credential rejected",
    errorBlob:
      "The custom AI credential (BYOK) for this model was rejected by the " +
      "provider — the API key is likely invalid, expired, or missing access " +
      "to this model. Update the credential in AI credentials settings, then " +
      "run the node again.",
    isCodeBlock: false,
  };
};
